import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  RHYTHM_EVENT_TAB_ID,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';

/**
 * Backs the delete and reset buttons of the ECG bookmark view, tracking which bookmarks are marked for deletion. Disables the buttons when nothing is selected, shows the reset button once the selection or any multi-selected strip appears in the pending delete list, and binds the D key to delete while the rhythm event tab area is hovered.
 *
 * @returns An object carrying isDisableButton, isShowResetButton, handleDeleteBookmark and handleResetBookmark.
 */
const useBookmarkButtonTypes = () => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const ecgBookmarkDeleteIds = useReportStore((s) => s.ecgBookmarkDeleteIds);
  const setEcgBookmarkDeleteIds = useReportStore((s) => s.actions.setEcgBookmarkDeleteIds);

  // selectedStripState (Recoil, Task 7) types id as string | number; ecgBookmarkDeleteIdsState is string[] - atom-type gap.
  const isDeletedBookmark = ecgBookmarkDeleteIds.includes(selectedStrip?.id as string);
  const isNotSelectedStrip = !selectedStrip?.id;
  const isEmptyList = _.isEmpty(selectedMultipleStrip) && isNotSelectedStrip;
  const isDisableButton = isEmptyList || isNotSelectedStrip;

  const isShowResetButton = useMemo(
    () =>
      !!isDeletedBookmark ||
      selectedMultipleStrip.find((strip: any) => ecgBookmarkDeleteIds.includes(strip.id)),
    [isDeletedBookmark, selectedMultipleStrip, ecgBookmarkDeleteIds],
  );

  /**
   * Marks the selected ECG bookmarks for deletion. With a multi-selection it merges those strips with the single selection, de-duplicates them by id and appends the ids to the pending delete list, otherwise it appends only the selected strip's id.
   *
   * @returns Nothing.
   */
  const handleDeleteBookmark = () => {
    if (selectedMultipleStrip.length > 0) {
      const mergeData = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
      const listIds = _.map(mergeData, (strip: any) => strip.id);
      setEcgBookmarkDeleteIds((prev) => _.uniq([...prev, ...listIds]));
      return;
    }
    if (selectedStrip?.id) {
      setEcgBookmarkDeleteIds((prev) => _.uniq([...prev, selectedStrip.id as string]));
    }
  };

  /**
   * Takes the selected ECG bookmarks back out of the pending delete list. Pulls every multi-selected strip id from the list, and separately pulls the single selected strip's id.
   *
   * @returns Nothing.
   */
  const handleResetBookmark = () => {
    if (selectedMultipleStrip.length > 0) {
      const listIds = _.map(selectedMultipleStrip, (strip: any) => strip.id);
      setEcgBookmarkDeleteIds((prev) => {
        const clonePrev = [...prev];
        return _.pullAll(clonePrev, listIds);
      });
    }
    if (selectedStrip?.id) {
      setEcgBookmarkDeleteIds((prev) => {
        const clonePrev = [...prev];
        return _.pullAll(clonePrev, [selectedStrip.id as string]);
      });
    }
  };

  useEffect(() => {
    /**
     * Maps the D key to deleting the selected ECG bookmarks while the rhythm event tab area is hovered. Ignores auto-repeat, a pointer outside the tab zone and modifier-held presses, and only fires when the delete button is enabled.
     *
     * @param event - The window keydown event.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(RHYTHM_EVENT_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      if (keyCode === 68) {
        // Delete (D)
        if (!isDisableButton) {
          handleDeleteBookmark();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMultipleStrip, selectedStrip]);

  return {
    isDisableButton,
    isShowResetButton,
    handleDeleteBookmark,
    handleResetBookmark,
  };
};

export default useBookmarkButtonTypes;
